import React, { useEffect, useMemo, useState } from "react";
import { Typography } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import DoneIcon from "@mui/icons-material/Done";
import Button from "@mui/material/Button";
import { useTranslate } from "../../../hooks/useTranslate";
import axiosManager from "../../../services/axiosManager";
import Preloader from "../../../components/Preloader";
import SubjectButton from "./SubjectsButton";
import subjectSelectionService from "../../../services/subjectSelectionService";
import Dialog from "../../../components/Dialog";
import IconAlert from "../../../assets/images/icons/alert.svg?react";
import IconInfo from "../../../assets/images/icons/info.svg?react";
import TaskCard from "../../../components/TaskCard";
import axiosManagerRegister from "../../../services/axiosManagerRegister";
import { useToast } from "../../../context/toast";

export const PackageSubjectsSelect = (props) => {
    const {
        registrationId, childId, studySchedule, studentName,
        desiredStartDate,
        divisionSelected, yearGroupSelected, packageSelected, onPackageChange,
        subjectsSelected, onSubjectChange,
        onPrev, onNext,
    } = props;
    const { t } = useTranslate();
    const toast = useToast();

    const [showCheckModal, setShowCheckModal] = useState(false);

    const {
        isFetching: isLoadingProduct, data: product,
    } = useQuery({
        queryKey: [`add-student/${yearGroupSelected}/${divisionSelected}/packages`, desiredStartDate, studySchedule],
        queryFn: () => axiosManagerRegister.post$(`/api/api/registration/${registrationId}/product`, {
            studySchedule, divisionId: divisionSelected, yearGroupId: yearGroupSelected, startDate: desiredStartDate,
        }).then((response) => response?.data?.packages),
        retry: 0,
        enabled: !!yearGroupSelected && !!divisionSelected && !!registrationId && !!studySchedule,
    });

    const {
        mutateAsync: postSelectProduct, isPending: isPostingSelectProduct,
    } = useMutation({
        mutationFn: (requestData) => axiosManagerRegister.post$(`/api/api/registration/${registrationId}/product/select`, requestData),
        onError: () => {
            toast.error(t("common.errorMessage"));
        },
    });

    const {
        mutateAsync: postSelectSubject, isPending: isPostingSelectSubject,
    } = useMutation({
        mutationFn: (requestData) => axiosManagerRegister.post$(`/api/api/registration/${registrationId}/subject`, requestData),
        onError: () => {
            toast.error(t("common.errorMessage"));
        },
    });
    const {
        mutateAsync: deleteSubject, isPending: isDeletingSubject,
    } = useMutation({
        mutationFn: (requestData) => axiosManagerRegister.delete$(`/api/api/registration/${registrationId}/subject`, requestData),
        onError: () => {
            toast.error(t("common.errorMessage"));
        },
    });
    const {
        mutateAsync: confirmSubjects, isPending: isConfirmingSubjects,
    } = useMutation({
        mutationFn: () => axiosManagerRegister.post$(`/api/api/registration/${registrationId}/subject/confirm`, { childId }),
        onError: () => {
            toast.error(t("common.errorMessage"));
        },
    });

    const isActionDisabled = isPostingSelectSubject || isPostingSelectProduct || isDeletingSubject || isConfirmingSubjects;

    const subjectSections = useMemo(() => {
        if (!packageSelected) return [];
        return subjectSelectionService.getSections(packageSelected);
    }, [packageSelected, packageSelected]);
    const areRequiredSelected = useMemo(() => {
        if (!packageSelected) return false;
        return subjectSelectionService.checkIfRequiredSelected(packageSelected, subjectsSelected);
    }, [packageSelected, packageSelected, subjectsSelected]);
    const hasHigherSubjects = useMemo(() => (packageSelected ? subjectSelectionService.hasHigherSubjects(packageSelected, subjectsSelected) : false), [packageSelected, packageSelected, subjectsSelected]);

    const subjectNames = useMemo(() => subjectSelectionService
        .getSelectedSubjects(packageSelected, subjectsSelected)
        .map((s) => s.name)
        .join("; "), [packageSelected, subjectsSelected]);

    const modalCheck = useMemo(() => {
        if (!packageSelected) return null;
        const {
            hasAbsoluteMaxSubjects,
            hasIncludedMaxSubjects,
            hasRecommendedMaxSubjects,
        } = subjectSelectionService.checkLimits(packageSelected, subjectsSelected);
        const { includedMaxSubjects, recommendedMaxSubjects } = packageSelected || {};
        if (hasAbsoluteMaxSubjects) {
            return {
                title: t("subjects.absoluteMaxSubjects.title"),
                description: t("subjects.absoluteMaxSubjects.subtitle", { num: includedMaxSubjects }),
                cancellationText: t("subjects.absoluteMaxSubjects.cancel"),
                confirmationText: t("subjects.absoluteMaxSubjects.confirm"),
                onCancel: () => setShowCheckModal(false),
                onConfirm: onNext,
                confirmDisabled: !areRequiredSelected,
            };
        }
        if (hasIncludedMaxSubjects) {
            return {
                title: t("subjects.includedMaxSubjectsModal.title"),
                description: t("subjects.includedMaxSubjectsModal.subtitle", { num: includedMaxSubjects }),
                cancellationText: t("subjects.includedMaxSubjectsModal.cancel"),
                confirmationText: t("subjects.includedMaxSubjectsModal.confirm"),
                onCancel: () => setShowCheckModal(false),
                onConfirm: onNext,
                confirmDisabled: !areRequiredSelected,
            };
        }

        if (hasRecommendedMaxSubjects) {
            return {
                title: t("subjects.recommendedMaxSubjectsModal.title", { num: recommendedMaxSubjects }),
                description: t("subjects.recommendedMaxSubjectsModal.subtitle"),
                dialogType: "error",
                confirmationText: t("subjects.recommendedMaxSubjectsModal.confirm"),
                onConfirm: () => setShowCheckModal(false),
            };
        }

        return null;
    }, [packageSelected, subjectsSelected, t, onNext]);

    const onPackageClick = async (packageProduct) => {
        try {
            await postSelectProduct({
                onlineRegistrationId: registrationId,
                childId,
                productId: packageProduct.productId,
            });
            onPackageChange(packageProduct);
            const lockedSubjects = subjectSelectionService.getLockedSubjects(packageProduct);
            onSubjectChange(lockedSubjects.map((x) => x.productId));
        } catch {
            // Error is in Mutation
        }
    };
    const handleSubjectSelection = async (subjectId) => {
        const wasSelected = subjectsSelected.includes(subjectId);
        if (wasSelected) {
            try {
                await deleteSubject({ onlineRegistrationId: registrationId, childId, subjectId });
                onSubjectChange(subjectsSelected.filter((item) => item !== subjectId));
            } catch {
                // Error is in Mutation
            }
        } else {
            try {
                await postSelectSubject({
                    onlineRegistrationId: registrationId, childId, productId: packageSelected?.productId, subjectId,
                });
                onSubjectChange([...subjectsSelected, subjectId]);
            } catch {
                // Error is in Mutation
            }
        }
    };
    const onSubjectClick = (subject) => {
        const isSelectedHigher = subject.higherSubject && subjectsSelected.includes(subject.higherSubject.productId);

        if (isSelectedHigher && subject.higherSubject) {
            handleSubjectSelection(subject.higherSubject.productId);
        } else {
            handleSubjectSelection(subject.productId);
        }
    };
    const onBackClick = () => {
        onPackageChange(null);
        onSubjectChange([]);
        onPrev?.();
    };
    const onNextClick = async () => {
        try {
            if (!hasHigherSubjects) await confirmSubjects();
            onNext?.();
        } catch {
            //
        }
    };

    useEffect(() => {
        if (modalCheck) setShowCheckModal(true);
    }, [!!modalCheck]);

    useEffect(() => {
        if (!packageSelected && !!product?.length) {
            onPackageClick(product[0]);
        }
        if (packageSelected?.productId && !packageSelected.name && !!product?.length) {
            const fullPackage = product.find((item) => item.productId === packageSelected.productId);
            onPackageChange(fullPackage);
        }
    }, [product, packageSelected]);

    return (
        <div className="details-form">
            <Typography variant="h1">
                {t("addStudent.selectPackage")}
            </Typography>
            <Typography variant="h2" className="mt-8">
                {t("addStudent.fullTime")}
            </Typography>
            <Typography variant="body1" component="div">
                {t("addStudent.fullTimeSub")}
            </Typography>
            <Typography variant="h2" className="mt-8">
                {t("addStudent.3SubjectsPackage")}
            </Typography>
            <Typography variant="body1" component="div">
                {t("addStudent.3SubjectsPackageSub")}
            </Typography>
            <Preloader isLoading={isLoadingProduct} size={40}>
                <Typography variant="h3" className="add-student__title-boxed mt-8">
                    {t("addStudent.selectAPackage")}
                </Typography>
                <div className="flex flex-wrap align-stretch gap-8 mt-4">
                    {(product || []).map((packageProduct) => (
                        <button
                            key={packageProduct.productId}
                            type="button"
                            disabled={isActionDisabled}
                            className={`add-student-selection-option add-student-selection-option--division ${packageProduct.productId === packageSelected?.productId ? "selected" : ""}`}
                            onClick={() => onPackageClick(packageProduct)}
                        >
                            <span className="add-student-selection-option__icon">
                                {packageProduct.productId === packageSelected?.productId && <DoneIcon />}
                            </span>
                            <span className="add-student-selection-option__primary-text">{packageProduct.name}</span>
                        </button>
                    ))}
                </div>
                {packageSelected && (
                    <div className="mt-8">
                        <Typography variant="h3" className="add-student__title-boxed">
                            {t("addStudent.includedSubjects")}
                        </Typography>
                        <div>
                            {(subjectSections || []).map((section, idx) => (
                                <div key={section.productId} className="w-full mt-4">
                                    <Typography variant="h5" component="div" className="text-primary">
                                        <b className="font-bold">{section.name}</b>
                                    </Typography>
                                    <Typography variant="body1" component="div" className="text-primary">
                                        {section.instructions}
                                    </Typography>
                                    <div className="flex flex-wrap align-stretch gap-4 mt-4">
                                        {(section.subjects || []).map((subject) => (
                                            <SubjectButton
                                                key={subject.productId}
                                                subject={subject}
                                                disabled={isActionDisabled}
                                                packageProduct={packageSelected}
                                                subjectsSelected={subjectsSelected}
                                                onSubjectClick={onSubjectClick}
                                            />
                                        ))}
                                    </div>
                                    {idx !== subjectSections.length - 1 && <div className="divider mt-4" />}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </Preloader>
            <TaskCard title={t("addStudent.subjectSummary", { name: studentName })} className="mt-12">
                <Typography variant="body1" component="div" className="text-primary">
                    <b>{`${t("addStudent.selectedSubjects")}: `}</b>
                    {subjectNames || "-"}
                </Typography>
                <div className="add-student__form-narrow flex align-center gap-8 mt-4">
                    <Button
                        variant="outlined"
                        className="add-student-form__button"
                        onClick={onBackClick}
                        disabled={isActionDisabled || isLoadingProduct}
                    >
                        {t("button.back")}
                    </Button>
                    <Button
                        variant="contained"
                        className="button-accent add-student-form__button"
                        disabled={isLoadingProduct || !packageSelected || !areRequiredSelected}
                        loading={isConfirmingSubjects}
                        onClick={onNextClick}
                    >
                        {t("button.next")}
                    </Button>
                </div>
            </TaskCard>

            {modalCheck && (
                <Dialog
                    open={!!showCheckModal}
                    onClose={() => setShowCheckModal(false)}
                    className={`subject-dialog ${modalCheck.dialogType === "error" ? "subject-dialog--error" : ""}`}
                >
                    {modalCheck.dialogType === "error"
                        ? <IconAlert className="subject-dialog__icon" />
                        : <IconInfo className="subject-dialog__icon" />}
                    <Typography variant="h5" component="h3" className="subject-dialog__title">
                        {modalCheck.title}
                    </Typography>
                    <Typography variant="body1" component="div" className="subject-dialog__description">
                        {modalCheck.description}
                    </Typography>
                    <div className="subject-dialog__footer">
                        {modalCheck.cancellationText && (
                            <Button
                                className="button-accent subject-dialog__btn"
                                onClick={modalCheck.onCancel}
                            >
                                {modalCheck.cancellationText}
                            </Button>
                        )}
                        {modalCheck.confirmationText && (
                            <Button
                                className="button-accent subject-dialog__btn"
                                onClick={modalCheck.onConfirm}
                                disabled={modalCheck.confirmDisabled}
                            >
                                {modalCheck.confirmationText}
                            </Button>
                        )}
                    </div>
                </Dialog>
            )}
        </div>
    );
};
